import React from 'react';
import { View, Text, Flex } from '@smarterservices/smarter-ui';

interface KeyboardShortcutDisplayProps {
  shortcut: string; // Single shortcut like "Ctrl+C" or "B"
  textVariant?: 'legend' | 'contentSmall' | 'contentImportant' | 'content' | 'descriptionSection';
  margin?: string;
  variant?: 'light' | 'dark' | 'medium';
  keyBackgroundColor?: string;
  keyTextColor?: string;
  borderRadius?: string;
  keyPadding?: string;
  showSeparator?: boolean; // Whether to show "+" between keys
}

export function KeyboardShortcutDisplay({ 
  shortcut,
  textVariant = 'legend',
  margin = 'none',
  variant = 'medium',
  keyBackgroundColor,
  keyTextColor,
  borderRadius = 'small',
  keyPadding = 'xxx-small xx-small',
  showSeparator = false
}: KeyboardShortcutDisplayProps) {
  if (!shortcut) {
    return null;
  }

  // Determine colors based on variant
  const getBackgroundColor = () => {
    if (keyBackgroundColor) return keyBackgroundColor;
    switch (variant) {
      case 'dark':
        return 'primary';
      case 'medium':
        return 'secondary';
      case 'light':
        return 'info';
      default:
        return 'primary-inverse';
    }
  };

  const getTextColor = () => {
    if (keyTextColor) return keyTextColor;
    switch (variant) {
      case 'light':
        return 'primary-inverse';
      case 'medium':
        return 'primary';
      case 'dark':
      default:
        return 'primary';
    }
  };

  const getBorderColor = () => {
    return variant === 'medium' ? 'primary' : 'transparent';
  };

  const getPlusTextColor = () => {
    if (keyTextColor) return keyTextColor;
    return variant === 'dark' ? 'primary-inverse' : 'primary';
  };

  // Detect operating system
  const getOS = (): 'mac' | 'windows' | 'linux' | 'unknown' => {
    if (typeof navigator === 'undefined') return 'unknown';
    
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('mac')) return 'mac';
    if (userAgent.includes('win')) return 'windows';
    if (userAgent.includes('linux')) return 'linux';
    return 'unknown';
  };

  // Map key names to visual representations based on OS
  const getKeyDisplay = (key: string): string => {
    const os = getOS();
    
    // Common keys that are the same across all OS
    const commonMappings: { [key: string]: string } = {
      // Arrow keys
      'ArrowLeft': '←',
      'ArrowRight': '→', 
      'ArrowUp': '↑',
      'ArrowDown': '↓',
      'LeftArrow': '←',
      'RightArrow': '→',
      'UpArrow': '↑',
      'DownArrow': '↓',
      
      // Universal special keys
      'Space': '⎵',
      'Enter': '↵',
      'Return': '↵',
      'Tab': '⇥',
      'Escape': '⎋',
      'Esc': '⎋',
      
      // Function keys
      'F1': 'F1', 'F2': 'F2', 'F3': 'F3', 'F4': 'F4',
      'F5': 'F5', 'F6': 'F6', 'F7': 'F7', 'F8': 'F8',
      'F9': 'F9', 'F10': 'F10', 'F11': 'F11', 'F12': 'F12',
    };

    // OS-specific modifier keys
    const osSpecificMappings: { [os: string]: { [key: string]: string } } = {
      mac: {
        'Shift': '⇧',
        'Ctrl': '⌃',
        'Control': '⌃',
        'Alt': '⌥',
        'Option': '⌥',
        'Cmd': '⌘',
        'Command': '⌘',
        'Meta': '⌘',
        'Backspace': '⌫',
        'Delete': '⌦',
      },
      windows: {
        'Shift': 'Shift',
        'Ctrl': 'Ctrl',
        'Control': 'Ctrl',
        'Alt': 'Alt',
        'Win': '⊞',
        'Windows': '⊞',
        'Meta': '⊞',
        'Backspace': '⌫',
        'Delete': 'Del',
      },
      linux: {
        'Shift': 'Shift',
        'Ctrl': 'Ctrl',
        'Control': 'Ctrl',
        'Alt': 'Alt',
        'Super': 'Super',
        'Meta': 'Super',
        'Backspace': '⌫',
        'Delete': 'Del',
      },
      unknown: {
        'Shift': 'Shift',
        'Ctrl': 'Ctrl',
        'Control': 'Ctrl',
        'Alt': 'Alt',
        'Meta': 'Meta',
        'Cmd': 'Cmd',
        'Command': 'Cmd',
        'Option': 'Option',
        'Backspace': '⌫',
        'Delete': 'Del',
      }
    };

    // Check common mappings first
    if (commonMappings[key]) {
      return commonMappings[key];
    }

    // Then check OS-specific mappings
    const osMapping = osSpecificMappings[os] || osSpecificMappings.unknown;
    return osMapping[key] || key;
  };

  // Split the shortcut on '+' to handle multi-key combinations
  const keys = shortcut.split ? shortcut.split('+').map(key => getKeyDisplay(key.trim())) : [];

  return (
    <View as="span" margin={margin} display="inline-flex" alignItems="center">
      {keys.map((key, index) => (
        <React.Fragment key={index}>
          <View 
            as="span" 
            display="inline-flex" 
            alignItems="center"
            background={getBackgroundColor()}
            minWidth="23px"
            padding={keyPadding}
            textAlign="center"
            borderRadius={borderRadius}
            borderWidth={variant === 'medium' ? 'small' : 'none'}
            borderColor={getBorderColor()}
            margin={!showSeparator && index < keys.length - 1 ? 'none xxx-small none none' : 'none'}
            themeOverride={{
              paddingXxSmall: '.275rem',
              paddingXxxSmall: '.020rem',
              backgroundInfo: '#000000',
            }}
          >
            <View as="div" width="100%" alignItems="center">
              <Text variant={textVariant} 
                    color={getTextColor()} 
                    weight="bold">
                {key}
              </Text>
            </View>
          </View>
          {showSeparator && index < keys.length - 1 && (
            <View as="span" margin="none xxx-small" display="inline-flex" alignItems="center">
              <Text variant={textVariant} color={getPlusTextColor()}>
                +
              </Text>
            </View>
          )}
        </React.Fragment>
      ))}
    </View>
  );
}

export default KeyboardShortcutDisplay;
